import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import * as Highcharts from "highcharts";
import HCNoData from 'highcharts/modules/no-data-to-display';
import HC_sankey from 'highcharts/modules/sankey';
import HC_depwheel from 'highcharts/modules/dependency-wheel';
import HC_more from 'highcharts/highcharts-more';
import {TranslocoService} from "@ngneat/transloco";
import HC_export from 'highcharts/modules/exporting';
import HC_export_offline from 'highcharts/modules/offline-exporting';
import HC_exportData from 'highcharts/modules/export-data';
import {FileExtensionType} from "../../../model/file-extension-type";
import {AuditsService} from "../../../services/audits.service";
import {ExportFileType} from "../../../model/export-file-type";

HC_more(Highcharts);
HC_sankey(Highcharts);
HC_depwheel(Highcharts);
HCNoData(Highcharts);
HC_export(Highcharts);
HC_export_offline(Highcharts);
HC_exportData(Highcharts);

@Component({
  selector: 'app-generic-graph',
  templateUrl: './generic-graph.component.html',
  styleUrls: ['./generic-graph.component.scss']
})
export class GenericGraphComponent implements OnChanges {

  @Input() chartType = 'column';
  @Input() dataType = 'datetime';
  @Input() name: string;
  @Input() height = 225;
  @Input() width = null;
  @Input() title = '';
  @Input() fileName: string = '';
  @Input() exportFileType: ExportFileType;
  @Input() machineSerialNumber: string;
  @Input() dataDescription: string = null;
  @Input() tooltipDecimalPlaces = 0;
  @Input() customSeries = false;
  @Input() legend = false;
  @Input() bottomLegend = false;
  @Input() chartInverted = false;
  @Input() chartPolar = false;
  @Input() plotOptions: any = {};
  @Input() categories = undefined;
  @Input() tooltipDateFormat: string = undefined;
  @Input() pointFormat: string = undefined;
  @Input() tooltipShared = false;
  @Input() yAxis: any = {title: {enabled: false}};
  @Input() xAxis: any = undefined;
  @Input() pane: any = {}
  @Input() update = false;
  @Output() updateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public Highcharts: typeof Highcharts = Highcharts;

  _data: any[];

  public chartOptions;
  public chart;

  private legendNavigation;
  private legendInfo;

  private language: string;


  constructor(private i18n: TranslocoService,
              private auditsService: AuditsService) {
  }

  ngOnChanges(): void {
    this.language = this.i18n.getActiveLang();
    this.setOptions();
    this.setColor();
    this.buildLegend();
    this.buildGraph();
  }

  @Input()
  public set data(data: any[]) {
    this._data = data;
    this.buildGraph();
  }

  public hcCallback(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  public getCallbackWithContext(): (chart: Highcharts.Chart) => void {
    return (chart: Highcharts.Chart) => this.hcCallback(chart);
  }

  public resize(): void {
    try {
      setTimeout(() => this.chart.reflow());
    } catch (ignored) {
    }
  }

  public download(chart: Highcharts.Chart): void {
    chart.exportChartLocal({filename: this.fileName});
    this.auditsService.createDownloadGraphAudit(this.exportFileType, FileExtensionType.PNG, this.machineSerialNumber).subscribe();
  }

  private buildGraph(): void {
    this.chartOptions = {
      chart: {
        type: this.chartType,
        inverted: this.chartInverted,
        height: this.height,
        width: this.width,
        zoomType: 'x',
        polar: this.chartPolar,
        resetZoomButton: {
          position: {
            x: 0,
            y: 160
          }
        }
      },
      pane: this.pane,
      plotOptions: this.plotOptions,
      title: {
        text: this.title
      },
      xAxis: this.xAxis ? this.xAxis : {
        type: this.dataType,
        categories: this.categories
      },
      yAxis: this.yAxis,
      legend: this.legendInfo,
      tooltip: {
        valueDecimals: this.tooltipDecimalPlaces,
        xDateFormat: this.tooltipDateFormat,
        shared: this.tooltipShared,
        animation: false,
        pointFormat: this.pointFormat
      },
      credits: {
        enabled: false
      },
      lang: {
        noData: this.i18n.translate('error.data.noData')
      },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      },
      exporting: {
        filename: this.title,
        sourceWidth: 1200,
        sourceHeight: 400,
      }
    };

    if (this.customSeries) {
      this.chartOptions.series = this._data;
    } else {
      this.chartOptions.series = [{name: this.name, data: this._data}];
    }
  }

  private setOptions(): void {
    let thousandsSeparator: string = this.language === 'en' ? ',' : '.';
    Highcharts.setOptions({
      lang: {
        loading: 'Cargando...',
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        rangeSelectorFrom: 'Desde',
        rangeSelectorTo: 'Hasta',
        rangeSelectorZoom: 'Período',
        downloadPNG: 'Descargar imagen PNG',
        downloadJPEG: 'Descargar imagen JPEG',
        downloadPDF: 'Descargar imagen PDF',
        downloadSVG: 'Descargar imagen SVG',
        printChart: 'Imprimir',
        resetZoom: 'Reiniciar zoom',
        resetZoomTitle: 'Reiniciar zoom',
        thousandsSep: this.language === 'fr' ? '' : thousandsSeparator,
        decimalPoint: this.language === 'en' ? '.' : ','
      }
    });
  }

  private setColor(): void {
    let docStyle = document.documentElement.style;
    const primaryColor = docStyle.getPropertyValue('--primary-color');
    const primaryLightColor = docStyle.getPropertyValue('--primary-light-color');
    this.legendNavigation = {
      activeColor: this.rgbToHex(primaryColor),
      inactiveColor: this.rgbToHex(primaryLightColor)
    }
  }

  private buildLegend(): void {
    if (!this.bottomLegend) {
      this.legendInfo = {
        align: 'right',
        verticalAlign: 'top',
        layout: 'vertical',
        itemMarginTop: 2,
        enabled: this.legend,
        navigation: this.legendNavigation
      }
    } else {
      this.legendInfo = {
        align: 'center',
        verticalAlign: 'bottom',
        enabled: this.legend,
        navigation: this.legendNavigation
      }
    }
  }

  private componentToHex(c): string {
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  }

  private rgbToHex(color: string): string {
    let rgb = color.substring(4, color.length - 1)
      .replace(/ /g, '')
      .split(',');
    return "#" + this.componentToHex(parseInt(rgb[0])) + this.componentToHex(parseInt(rgb[1])) + this.componentToHex(parseInt(rgb[2]));
  }

  updateChangeEmitter(value: boolean): void {
    this.updateChange.emit(value);
  }

}
